import { defineStore } from 'pinia'
import { notificationsService } from '@/services/notifications'
import { errorMessage } from '@/services/http'
import type { Notification } from '@/types/api'

let queued = false

interface NotificationsState {
  items: Notification[]
  loading: boolean
  ready: boolean
  error: string | null
}

export const useNotificationsStore = defineStore('notifications', {
  state: (): NotificationsState => ({
    items: [],
    loading: false,
    ready: false,
    error: null,
  }),

  getters: {
    unread: (state): number => state.items.filter((n) => n.read_date === null).length,
  },

  actions: {
    async fetch(): Promise<void> {
      if (this.loading) {
        queued = true
        return
      }

      this.loading = true
      this.error = null
      try {
        this.items = await notificationsService.list()
        this.ready = true
      } catch (e) {
        this.error = errorMessage(e)
      } finally {
        this.loading = false
      }

      if (queued) {
        queued = false
        await this.fetch()
      }
    },

    async fetchOnce(): Promise<void> {
      if (this.ready || this.loading) return
      await this.fetch()
    },

    async clearAll(): Promise<void> {
      if (!this.items.length) return
      this.items = []
      try {
        await notificationsService.clearAll()
      } catch (e) {
        this.error = errorMessage(e)
      }
    },

    async markAllRead(): Promise<void> {
      if (!this.unread) return

      const stamp = new Date().toISOString()
      for (const item of this.items) {
        if (item.read_date === null) item.read_date = stamp
      }

      try {
        await notificationsService.markAllRead()
      } catch (e) {
        this.error = errorMessage(e)
      }
    },
  },
})
